/**
 * Adhoc Term Sheet Schemas
 *
 * Zod validation schemas for the upload-your-own-PDF term sheet flow.
 * Issue #1393 — design decisions D5, D9, D10, D11, D14.
 */

import { z } from 'zod';

// ============================================================================
// ENUMS / PRIMITIVES
// ============================================================================

/**
 * Discriminator for the provenance of a DocumentTemplate row.
 * - `authored` — admin-authored reusable template
 * - `adhoc` — one-shot PDF tied to a specific loan case (filtered from template lists)
 * - `system` — seeded system template
 */
export const DocumentTemplateSourceSchema = z.enum(['authored', 'adhoc', 'system']);
export type DocumentTemplateSource = z.infer<typeof DocumentTemplateSourceSchema>;

/**
 * Signer role enum, mirrors `1-shared-types/src/enums/signer-role.ts`
 * but expressed as Zod for tRPC input validation.
 */
export const AdhocSignerRoleSchema = z.enum([
  'borrower',
  'co_borrower',
  'guarantor',
  'authorized_officer',
  'lender_signer',
  'credit_manager',
  'witness',
  'notary',
  'cc',
]);
export type AdhocSignerRole = z.infer<typeof AdhocSignerRoleSchema>;

// ============================================================================
// FIELD DEFINITIONS (from DocuSeal builder onSave callback)
// ============================================================================

/**
 * Position of a field on the PDF (Issue #1430). DocuSeal uses
 * page-relative coordinates with `(0,0)` at the top-left of the page;
 * width/height are also fractions of the page size. We round-trip these
 * so audit + future re-rendering can place a recipient's mark exactly
 * where the lender designed it.
 */
export const AdhocFieldAreaSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  w: z.number().min(0).max(1),
  h: z.number().min(0).max(1),
  page: z.number().int().min(0),
});
export type AdhocFieldArea = z.infer<typeof AdhocFieldAreaSchema>;

/**
 * A single field the user placed in the DocuSeal editor (text, signature,
 * date, checkbox, etc.). Stored on the DocumentTemplate.fields JSON column.
 *
 * `submitter` and `areas` were added in Issue #1430. Without them, our
 * record of the user's design intent is lossy — DocuSeal's DB has the
 * full picture but we can't audit, render, or test against it.
 *
 * Both stay optional for backward-compat with rows persisted before the
 * widening; new writes always populate them.
 */
// Issue #1430 / B16: `name` is OPTIONAL (default empty string).
// DocuSeal's editor lets users drag-drop a field without typing a label
// (the field gets `name: ""` in DocuSeal's DB). The tRPC saveTemplate
// mutation then 400'd here on the very common "user just drew a
// signature box" flow. Type is the only required shape gate.
//
// Issue #1531 Phase 2 PR E follow-up: `uuid` carries the DocuSeal-assigned
// field UUID so the binding picker can identify which placed field the
// lender chose. Optional + default empty string for backward-compat with
// rows persisted before this widening (DocuSeal always assigns a UUID
// for new fields, but legacy snapshots may not have round-tripped it).
export const AdhocTemplateFieldSchema = z.object({
  name: z.string().max(200).default(''),
  type: z.string().min(1).max(50), // "text" | "signature" | "date" | "checkbox" | ...
  required: z.boolean(),
  submitter: z.string().min(1).max(100).optional(),
  areas: z.array(AdhocFieldAreaSchema).optional(),
  uuid: z.string().max(64).default(''),
});
export type AdhocTemplateField = z.infer<typeof AdhocTemplateFieldSchema>;

/**
 * A submitter slot defined in the DocuSeal editor (Issue #1430). When a
 * lender designs an adhoc template they create one slot per signer role
 * (e.g., "Borrower", "Lender", "Credit Manager") and assign each placed
 * signature/text field to a slot. The send-for-esign panel reads these
 * back to know which portal recipient picker to render per slot.
 */
export const AdhocTemplateSubmitterSchema = z.object({
  uuid: z.string().min(1).max(64),
  name: z.string().min(1).max(100),
});
export type AdhocTemplateSubmitter = z.infer<typeof AdhocTemplateSubmitterSchema>;

// ============================================================================
// PROCEDURE INPUTS (tRPC — termsheets.adhoc.*)
// ============================================================================

/**
 * `termsheets.adhoc.uploadPdf` — uploads the PDF to DocuSeal, returns the
 * DocuSeal-assigned template id. No DocumentTemplate row is written yet.
 *
 * Size limit of 10 MB is enforced at the Zod boundary. Base64 inflates the
 * binary by ~33% so a 10 MB PDF is ~13.5 MB on the wire; the API Gateway's
 * Fastify `bodyLimit` of 100 MB (2-api-gateway/src/main.ts) leaves plenty
 * of headroom (gap G11 in issue #1393).
 */
export const UploadAdhocPdfInputSchema = z.object({
  fileName: z.string().min(1).max(255),
  // Base64-encoded PDF bytes. Raw size (before base64 overhead) capped at 10 MB
  // → encoded length cap: 10 * 1024 * 1024 * 4 / 3 ≈ 13,981,013 chars.
  fileBase64: z.string().max(13_981_013, 'PDF must be 10 MB or smaller'),
  caseId: z.string().uuid(),
});
export type UploadAdhocPdfInput = z.infer<typeof UploadAdhocPdfInputSchema>;

export const UploadAdhocPdfOutputSchema = z.object({
  pdfAttachmentId: z.string(),
  docuSealTemplateId: z.number().int().positive(),
});
export type UploadAdhocPdfOutput = z.infer<typeof UploadAdhocPdfOutputSchema>;

// G11 (Issue #1430 Phase 5): the legacy `GetBuilderTokenInputSchema` /
// `GetBuilderTokenOutputSchema` (with `mockMode: z.boolean()`) and
// related `getBuilderToken` tRPC route were removed when Phase 3
// switched to the iframe-based DocuSeal editor. Schemas removed here
// to keep the public surface clean — no consumers remain.

/**
 * `termsheets.adhoc.saveTemplate` — persists the DocumentTemplate row with
 * `source='adhoc'` and links it to the loan case. Called from the DocuSeal
 * builder's `onSave` callback.
 */
export const SaveAdhocTemplateInputSchema = z.object({
  docuSealTemplateId: z.number().int().positive(),
  caseId: z.string().uuid(),
  name: z.string().min(1).max(200),
  validityDays: z.number().int().min(1).max(365),
  internalNote: z.string().max(2000).optional(),
  fields: z.array(AdhocTemplateFieldSchema),
  // Issue #1430 — submitter slots the user designed against in DocuSeal.
  // Drives the send-panel picker rows (one row per slot).
  submitters: z.array(AdhocTemplateSubmitterSchema).default([]),
});
export type SaveAdhocTemplateInput = z.infer<typeof SaveAdhocTemplateInputSchema>;

export const SaveAdhocTemplateOutputSchema = z.object({
  documentTemplateId: z.string().uuid(),
});
export type SaveAdhocTemplateOutput = z.infer<typeof SaveAdhocTemplateOutputSchema>;

/**
 * `termsheets.adhoc.getTemplateState` (Issue #1430) — fetches the
 * current placed-fields + submitters state of the adhoc template
 * directly from DocuSeal. The portal polls this while the editor
 * iframe is open so the "Continue" button enables once at least one
 * signature field has been placed and assigned to a submitter.
 */
export const GetTemplateStateInputSchema = z.object({
  docuSealTemplateId: z.number().int().positive(),
});
export type GetTemplateStateInput = z.infer<typeof GetTemplateStateInputSchema>;

export const GetTemplateStateOutputSchema = z.object({
  fields: z.array(AdhocTemplateFieldSchema),
  submitters: z.array(AdhocTemplateSubmitterSchema),
  /**
   * True when at least one signature field is placed and assigned to a
   * submitter. The Continue button on the upload+design step is gated
   * on this so users can't proceed with a template that has no place
   * for anyone to sign.
   */
  readyForSend: z.boolean(),
});
export type GetTemplateStateOutput = z.infer<typeof GetTemplateStateOutputSchema>;

/**
 * `termsheets.adhoc.send` — orchestrator that creates + sends a signing
 * envelope from the adhoc template. Reuses the existing `CreateEsignEnvelope`
 * + `SendEsignEnvelope` gRPC RPCs (D11 / gap G3) — no new envelope RPC.
 *
 * Multi-signer validation: at least one signer required (gap G10). The UI
 * disabled-state already prevents zero-signer submission; this Zod refine
 * is defense in depth so the backend rejects empty cohorts.
 */
export const AdhocSignerSchema = z.object({
  role: AdhocSignerRoleSchema,
  email: z.string().email(),
  name: z.string().min(1).max(200),
  phone: z.string().max(30).optional(),
  signingOrder: z.number().int().min(1).max(20),
  // Issue #1407 multi-signer: per-recipient cross-service identity links.
  // The portal's adhoc Send panel populates these so SigningRecipient.consumerId
  // / userId reach the DB via the proto fields added in c6789f34b. Without
  // them the consumer portal's listPendingSignaturesByConsumer query
  // (filters by consumerId == ctx.user.id) can't surface adhoc envelopes
  // to the borrower, and lender-side roles fail the userId-required gate
  // on the templated send path. Both fields are optional — the gateway
  // falls back to a case-mgmt lookup by email for borrower rows when
  // consumerId is omitted.
  consumerId: z.string().uuid().optional(),
  userId: z.string().uuid().optional(),
});
export type AdhocSigner = z.infer<typeof AdhocSignerSchema>;

export const SendAdhocInputSchema = z.object({
  documentTemplateId: z.string().uuid(),
  caseId: z.string().uuid(),
  signers: z.array(AdhocSignerSchema).min(1, 'At least one signer is required'),
  // Resolved values to pre-fill DocuSeal fields at envelope creation time.
  // Keyed by the DocuSeal field names the user created in the builder.
  // IMPORTANT: must NOT contain a `term_sheet_id` key — the webhook handler
  // uses that to opportunistically update a TermSheet row, which adhoc has none of.
  prefillValues: z.record(z.string(), z.unknown()).optional(),
  expiresAt: z.string().datetime().optional(),
});
export type SendAdhocInput = z.infer<typeof SendAdhocInputSchema>;

export const SendAdhocOutputSchema = z.object({
  envelopeId: z.string().uuid(),
  docuSealSubmissionId: z.number().int().positive(),
});
export type SendAdhocOutput = z.infer<typeof SendAdhocOutputSchema>;

// ============================================================================
// LIST DISCRIMINATED UNION (D10)
// ============================================================================

/**
 * Term Sheet tab list item — discriminated union of templated (TermSheet
 * records) and adhoc (SigningEnvelope rows linked to DocumentTemplate{source:
 * 'adhoc'}). See design decision D10 in issue #1393.
 */
export const TermSheetListItemAdhocSchema = z.object({
  source: z.literal('adhoc'),
  id: z.string().uuid(),
  envelopeId: z.string().uuid(),
  name: z.string(),
  status: z.string(), // SigningEnvelopeStatus (draft | sent | partially_signed | completed | voided | expired)
  createdAt: z.string().datetime(),
  signers: z.number().int().min(0),
});
export type TermSheetListItemAdhoc = z.infer<typeof TermSheetListItemAdhocSchema>;
